import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { FormDatePicker } from '@/components/form/FormDatePicker'
import { FormErrorSummary } from '@/components/form/FormErrorSummary'
import { FormInput } from '@/components/form/FormInput'
import { FormTextarea } from '@/components/form/FormTextarea'
import { Button } from '@/components/ui/Button'
import { Modal } from '@/components/ui/Modal'
import type { StudentProject } from '@/features/student/api/profile.api'
import { useProjectMutations } from '@/features/student/hooks/useProfile'
import { getErrorMessage, getFieldErrors } from '@/utils/errors'

const schema = z.object({
  title: z.string().min(1, 'Title is required'),
  description: z.string().optional(),
  role: z.string().optional(),
  start_date: z.string().optional(),
  end_date: z.string().optional(),
  project_url: z.union([z.url('Enter a valid URL'), z.literal('')]).optional(),
})

type FormValues = z.infer<typeof schema>

function toFormValues(item?: StudentProject): FormValues {
  return {
    title: item?.title ?? '',
    description: item?.description ?? '',
    role: item?.role ?? '',
    start_date: item?.startDate ?? '',
    end_date: item?.endDate ?? '',
    project_url: item?.projectUrl ?? '',
  }
}

export function ProjectSection({ items }: { items: StudentProject[] }) {
  const [editing, setEditing] = useState<StudentProject | 'new' | null>(null)
  const [apiError, setApiError] = useState<unknown>(null)
  const { create, update, remove } = useProjectMutations()

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: toFormValues() })

  useEffect(() => {
    reset(toFormValues(editing === 'new' || editing === null ? undefined : editing))
  }, [editing, reset])

  const isPending = create.isPending || update.isPending

  const onSubmit = handleSubmit((values) => {
    setApiError(null)
    const onSettled = { onSuccess: () => setEditing(null), onError: (e: unknown) => setApiError(e) }
    if (editing === 'new') {
      create.mutate(values, onSettled)
    } else if (editing) {
      update.mutate({ id: editing.id, payload: values }, onSettled)
    }
  })

  return (
    <div>
      {items.length === 0 && <p className="text-muted fs-13">No projects added yet.</p>}
      {items.map((item) => (
        <div
          key={item.id}
          className="d-flex justify-content-between align-items-start border-bottom py-2"
        >
          <div>
            <p className="fw-medium mb-0">{item.title}</p>
            {item.role && <p className="fs-12 text-muted mb-0">{item.role}</p>}
          </div>
          <div className="d-flex gap-2">
            <Button variant="ghost" onClick={() => setEditing(item)}>
              Edit
            </Button>
            <Button
              variant="danger"
              confirmTitle="Remove this project?"
              onClick={() => remove.mutate(item.id)}
            >
              Delete
            </Button>
          </div>
        </div>
      ))}
      <Button variant="secondary" className="mt-3" onClick={() => setEditing('new')}>
        Add Project
      </Button>

      <Modal
        show={editing !== null}
        onClose={() => setEditing(null)}
        title={editing === 'new' ? 'Add Project' : 'Edit Project'}
      >
        <FormErrorSummary
          errors={errors}
          serverErrors={getFieldErrors(apiError)}
          generalMessage={apiError ? getErrorMessage(apiError) : null}
        />
        <form onSubmit={(e) => void onSubmit(e)} noValidate>
          <FormInput control={control} name="title" label="Project Title" required />
          <FormInput control={control} name="role" label="Your Role" />
          <FormTextarea control={control} name="description" label="Description" />
          <FormDatePicker control={control} name="start_date" label="Start Date" />
          <FormDatePicker control={control} name="end_date" label="End Date" />
          <FormInput control={control} name="project_url" label="Project URL" type="url" />
          <Button type="submit" className="w-100" loading={isPending}>
            Save
          </Button>
        </form>
      </Modal>
    </div>
  )
}
